import React, { useEffect, useState } from 'react';
import { Button, Form, Input, DatePicker, Select, Row, Col } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchLiveRanges } from '../../../pace/pace-actions';
import { getLiveRangesSearch, getSpecialRangesSearch } from '../../../pace/pace-selectors';
import { useLiveRanges } from '../../../common/hooks/liveRanges';
import { SuffixIcon, TagRenderer } from '@med-fe/ui';
import { omit, get } from 'lodash';
import AdvancedSearchButton from '../../../common/AdvancedSearchButton';

const { RangePicker } = DatePicker;

function SearchLiveRanges({ activeTab, search, isAllSelected, setIsSearch }: any) {
  const { options, handleChange, currentValue, regions, setRegionIds, districts, setDistrictIds, filterDistricts } =
    useLiveRanges(activeTab, isAllSelected);

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const liveRangesSearch = useSelector(getLiveRangesSearch);
  const { base } = search || {};
  const [advancedSearch, setAdvancedSearch] = useState<boolean>(false);

  // Restore form values when returning to the tab or when Redux state changes
  useEffect(() => {
    if (liveRangesSearch && liveRangesSearch.search) {
      // Restore search parameters only if search flag is true
      form.setFieldsValue({
        employeeName: liveRangesSearch.employeeName || undefined,
        employeeId: liveRangesSearch.employeeId || undefined,
        office: liveRangesSearch.office || undefined,
        officeName: liveRangesSearch.officeName || undefined,
        regionIds: liveRangesSearch.regionIds || [],
        districtIds: liveRangesSearch.districtIds || [],
        odShiftLengthFrom: liveRangesSearch.odShiftLengthFrom || undefined,
        odShiftLengthTo: liveRangesSearch.odShiftLengthTo || undefined,
        officeLengthHOOFrom: liveRangesSearch.officeLengthHOOFrom || undefined,
        officeLengthHOOTo: liveRangesSearch.officeLengthHOOTo || undefined,
      });
      if (liveRangesSearch.regionIds && liveRangesSearch.regionIds.length > 0) {
        setRegionIds(liveRangesSearch.regionIds);
      } else {
        setRegionIds([]);
      }
      if (liveRangesSearch.districtIds && liveRangesSearch.districtIds.length > 0) {
        setDistrictIds(liveRangesSearch.districtIds);
      } else {
        setDistrictIds([]);
      }
      // Show advanced search if any advanced fields have values
      if (
        liveRangesSearch.odShiftLengthFrom ||
        liveRangesSearch.odShiftLengthTo ||
        liveRangesSearch.officeLengthHOOFrom ||
        liveRangesSearch.officeLengthHOOTo
      ) {
        setAdvancedSearch(true);
      } else {
        setAdvancedSearch(false);
      }
    } else {
      // Reset form when no search is active (e.g., switching tabs)
      form.resetFields();
      setRegionIds([]);
      setDistrictIds([]);
      setAdvancedSearch(false);
    }
  }, [activeTab, liveRangesSearch]);

  const onSubmit = (formData) => {
    const { ...data } = formData;
    const dates = {
      dateFrom: null,
      dateTo: null,
    };

    dispatch(
      setSearchLiveRanges({ ...liveRangesSearch, ...omit(data, 'searchReason'), ...dates, page: 0, search: true })
    );
    setIsSearch(true);
  };

  return (
    <div className='search-container'>
      <Form
        form={form}
        layout='vertical'
        fields={[]}
        initialValues={{}}
        // className='search-container'
        onFinish={onSubmit}
      >
        <Row wrap={false}>
          <Col>
            <Row wrap={false}>
              <Form.Item
                key='name'
                label={'Employee Name'}
                name='employeeName'
                className='label-text input-field input-field_name'
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                key='id'
                label={'Employee ID'}
                name='employeeId'
                className='label-text input-field input-field_employee-id'
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                key='office'
                label={'Office Number'}
                name='office'
                className='label-text input-field input-field_office-number'
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                key='officeName'
                label={'Office Name'}
                name='officeName'
                className='label-text input-field input-field_office-name'
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                style={{ width: '150px' }}
                key='regionIds'
                label={'Search by Region'}
                name='regionIds'
                className='label-text region-field'
              >
                <Select
                  style={{ width: '150px' }}
                  allowClear
                  mode={'multiple'}
                  maxTagCount={'responsive'}
                  tagRender={(props) => <TagRenderer {...props} />}
                  placeholder='...'
                  suffixIcon={<SuffixIcon />}
                  filterOption={(inputValue, option: any) => {
                    const region = option.value.split('(');
                    return get(region, '[0]').toUpperCase().includes(inputValue.toUpperCase());
                  }}
                  options={regions.map(({ id, name }) => ({ value: id, label: name }))}
                  onChange={(value, option: [] | any) => {
                    if (!option.length) {
                      setRegionIds([]);
                      setDistrictIds([]);
                      form.resetFields(['districtIds', 'regionIds']);
                    } else {
                      setRegionIds(value);
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                style={{ width: '150px', marginLeft: -50 }}
                key='districtIds'
                label={'Search by District'}
                name='districtIds'
                className='label-text district-field'
              >
                <Select
                  style={{ width: '150px' }}
                  allowClear
                  mode={'multiple'}
                  maxTagCount={'responsive'}
                  tagRender={(props) => <TagRenderer {...props} />}
                  placeholder='...'
                  suffixIcon={<SuffixIcon />}
                  options={districts.map(({ id, name, regionName }) => ({
                    value: id,
                    label: `${name} (${regionName})`,
                    key: id,
                  }))}
                  filterOption={(inputValue, option: any) => {
                    const district = option.name.split('(');
                    return get(district, '[0]').toUpperCase().includes(inputValue.toUpperCase());
                  }}
                  onChange={(value, option: [] | any) => {
                    if (!option.length) {
                      setDistrictIds([]);
                      form.setFieldsValue({ districtIds: [] });
                    } else {
                      setDistrictIds(value);
                    }
                  }}
                />
              </Form.Item>
              <Col style={{ display: 'flex', marginTop: '10px' }}>
                <Form.Item style={{ marginLeft: '-50px' }} className='search-btn'>
                  <Button type='primary' htmlType='submit'>
                    SEARCH
                  </Button>
                  <AdvancedSearchButton active={!advancedSearch} onClick={() => setAdvancedSearch(!advancedSearch)} />
                </Form.Item>
              </Col>
            </Row>
            {advancedSearch && (
              <Row wrap={false}>
                <Form.Item
                  key='odShiftLengthFrom'
                  label={'OD’s shift From'}
                  name='odShiftLengthFrom'
                  className='label-text input-field-number'
                >
                  <Input allowClear type='number' min={0} max={24} />
                </Form.Item>
                <Form.Item
                  key='odShiftLengthTo'
                  label={'OD’s shift To'}
                  name='odShiftLengthTo'
                  className='label-text input-field-number'
                >
                  <Input allowClear type='number' min={0} max={24} />
                </Form.Item>
                <Form.Item
                  key='officeLengthHOOFrom'
                  label={'Office HOO From'}
                  name='officeLengthHOOFrom'
                  className='label-text input-field-number'
                >
                  <Input allowClear type='number' min={0} max={24} />
                </Form.Item>
                <Form.Item
                  key='officeLengthHOOTo'
                  label={'Office HOO To'}
                  name='officeLengthHOOTo'
                  className='label-text input-field-number'
                >
                  <Input allowClear type='number' min={0} max={24} />
                </Form.Item>
              </Row>
            )}
          </Col>
        </Row>
      </Form>
    </div>
  );
}

export default SearchLiveRanges;
